import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Vote, Plus, Loader2, CheckCircle2, XCircle, Clock,
  Trash2, BarChart3, Users, Lock, X, MoreVertical, Calendar
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VotingPollPage() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [currentUserId, setCurrentUserId] = useState('');
  const [votingPollId, setVotingPollId] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [newPoll, setNewPoll] = useState({ title: '', description: '', options: ['', ''], is_multiple_choice: false, is_anonymous: false, deadline: '' });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUserId(user.id || '');
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/polls`, { headers: { Authorization: `Bearer ${token}` } });
      setPolls(response.data.polls);
      if (response.data.current_user_id) setCurrentUserId(response.data.current_user_id);
    } catch (error) { toast.error('Failed to load polls'); }
    finally { setLoading(false); }
  };

  const handleCreatePoll = async () => {
    const validOptions = newPoll.options.filter(o => o.trim());
    if (!newPoll.title.trim()) { toast.error('Please enter a title'); return; }
    if (validOptions.length < 2) { toast.error('Minimum 2 options required'); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { title: newPoll.title, description: newPoll.description, options: validOptions, is_multiple_choice: newPoll.is_multiple_choice, is_anonymous: newPoll.is_anonymous, deadline: newPoll.deadline || null };
      const response = await axios.post(`${API}/polls`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setPolls([response.data.poll, ...polls]);
      setShowAddModal(false);
      setNewPoll({ title: '', description: '', options: ['', ''], is_multiple_choice: false, is_anonymous: false, deadline: '' });
      toast.success('Poll created successfully!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create poll'); }
    finally { setSubmitting(false); }
  };

  const handleVote = async (pollId) => {
    if (selectedOptions.length === 0) { toast.error('Please select at least one option'); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/polls/${pollId}/vote`, { option_ids: selectedOptions }, { headers: { Authorization: `Bearer ${token}` } });
      setPolls(polls.map(p => p.id === pollId ? { ...p, options: response.data.options, total_voters: response.data.total_voters } : p));
      setVotingPollId(null);
      setSelectedOptions([]);
      toast.success('Vote submitted successfully!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to vote'); }
  };

  const handleClosePoll = async (pollId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/polls/${pollId}/close`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPolls(polls.map(p => p.id === pollId ? { ...p, status: 'closed' } : p));
      toast.success('Poll closed');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to close poll'); }
  };

  const handleDeletePoll = async (pollId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/polls/${pollId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPolls(polls.filter(p => p.id !== pollId));
      toast.success('Poll deleted');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete poll'); }
  };

  const toggleOption = (optionId, isMultiple) => {
    if (isMultiple) setSelectedOptions(prev => prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]);
    else setSelectedOptions([optionId]);
  };

  const addOptionField = () => { if (newPoll.options.length < 10) setNewPoll({ ...newPoll, options: [...newPoll.options, ''] }); };
  const removeOptionField = (index) => { if (newPoll.options.length > 2) setNewPoll({ ...newPoll, options: newPoll.options.filter((_, i) => i !== index) }); };

  const filteredPolls = polls.filter(p => filter === 'all' || p.status === filter);
  const hasVoted = (poll) => (poll.total_voters || []).includes(currentUserId);
  const getTotalVotes = (poll) => poll.options.reduce((sum, opt) => sum + (opt.votes || []).length, 0);

  const stats = {
    total: polls.length,
    active: polls.filter(p => p.status === 'active').length,
    closed: polls.filter(p => p.status === 'closed').length,
    totalVoters: polls.reduce((sum, p) => sum + (p.total_voters || []).length, 0)
  };

  if (loading) {
    return (<ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>);
  }

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        {/* Header */}
        <SectionHeader
          icon={Vote}
          title="Voting & Polls"
          subtitle="Create polls for team decisions"
          iconBg="bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/20"
          action={<Button data-testid="create-poll-btn" onClick={() => setShowAddModal(true)} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20"><Plus size={18} className="mr-2" /> New Poll</Button>}
        />

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Polls', value: stats.total, accent: 'border-[hsl(var(--border))]', vc: '' },
            { label: 'Active', value: stats.active, accent: 'border-emerald-500/30', vc: 'text-emerald-400' },
            { label: 'Closed', value: stats.closed, accent: 'border-red-500/30', vc: 'text-red-400' },
            { label: 'Total Voters', value: stats.totalVoters, accent: 'border-blue-500/30', vc: 'text-blue-400' },
          ].map((stat) => (
            <div key={stat.label} className={`bg-[hsl(var(--card))] border ${stat.accent} rounded-xl p-4`}>
              <p className={`text-xs ${stat.vc || 'text-[hsl(var(--text-muted))]'} mb-1`}>{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.vc}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {['all', 'active', 'closed'].map((f) => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} className={filter === f ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0' : 'border-[hsl(var(--border))] text-[hsl(var(--text-secondary))]'} data-testid={`poll-filter-${f}`}>
              {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Closed'}
            </Button>
          ))}
        </div>

        {/* Polls List */}
        <div className="space-y-4">
          {filteredPolls.length === 0 ? (
            <div data-testid="empty-polls" className="text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
              <Vote size={48} className="mx-auto text-emerald-500/30 mb-4" />
              <p className="text-[hsl(var(--text-secondary))] font-medium">No polls found</p>
              <p className="text-sm text-[hsl(var(--text-muted))] mt-1">Create a poll to start voting</p>
            </div>
          ) : (
            filteredPolls.map((poll) => {
              const voted = hasVoted(poll);
              const totalVotes = getTotalVotes(poll);
              const isActive = poll.status === 'active';
              const isVoting = votingPollId === poll.id;
              const isOwner = poll.user_id === currentUserId;
              const canManage = isOwner || (() => { try { return ['admin', 'developer', 'manager'].includes(JSON.parse(localStorage.getItem('user') || '{}').role_id); } catch { return false; } })();
              
              return (
                <div key={poll.id} data-testid={`poll-${poll.id}`} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 hover:border-emerald-500/20 transition-all">
                  {/* Poll Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className={isActive ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs' : 'bg-gray-500/10 text-gray-400 border border-gray-500/20 text-xs'}>
                        {isActive ? <CheckCircle2 size={10} className="mr-1" /> : <XCircle size={10} className="mr-1" />}
                        {isActive ? 'Active' : 'Closed'}
                      </Badge>
                      {poll.is_multiple_choice && <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs">Multi</Badge>}
                      {poll.is_anonymous && <Badge className="bg-purple-500/10 text-purple-400 border border-purple-500/20 text-xs"><Lock size={8} className="mr-1" /> Anon</Badge>}
                    </div>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical size={14} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                          {isActive && <DropdownMenuItem onClick={() => handleClosePoll(poll.id)}><XCircle size={14} className="mr-2" /> Close Poll</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => handleDeletePoll(poll.id)} className="text-red-400"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-[hsl(var(--text-primary))] mb-1">{poll.title}</h3>
                  {poll.description && <p className="text-sm text-[hsl(var(--text-muted))] mb-4">{poll.description}</p>}

                  {/* Options */}
                  <div className="space-y-2 mb-4">
                    {poll.options.map((opt) => {
                      const voteCount = (opt.votes || []).length;
                      const pct = totalVotes > 0 ? ((voteCount / totalVotes) * 100).toFixed(0) : 0;
                      const userVotedThis = (opt.votes || []).includes(currentUserId);
                      const showResults = voted || !isActive;
                      const isSelected = isVoting && selectedOptions.includes(opt.id);
                      
                      if (isVoting) {
                        return (
                          <button key={opt.id} onClick={() => toggleOption(opt.id, poll.is_multiple_choice)} className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${isSelected ? 'border-emerald-500 bg-emerald-500/5' : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:border-emerald-500/30'}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded-${poll.is_multiple_choice ? 'md' : 'full'} border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-[hsl(var(--border))]'}`}>
                                {isSelected && <CheckCircle2 size={12} className="text-white" />}
                              </div>
                              <span className="text-sm font-medium text-[hsl(var(--text-primary))]">{opt.text}</span>
                            </div>
                          </button>
                        );
                      }
                      
                      return (
                        <div key={opt.id} className={`relative p-3.5 rounded-xl border ${userVotedThis ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))]'} overflow-hidden`}>
                          {showResults && <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-transparent rounded-xl transition-all" style={{ width: `${pct}%` }} />}
                          <div className="relative flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {userVotedThis && <CheckCircle2 size={14} className="text-emerald-400" />}
                              <span className="text-sm font-medium text-[hsl(var(--text-primary))]">{opt.text}</span>
                            </div>
                            {showResults && <span className="text-sm font-semibold text-[hsl(var(--text-secondary))]">{pct}% <span className="text-xs font-normal text-[hsl(var(--text-muted))]">({voteCount})</span></span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-[hsl(var(--border))]">
                    <div className="flex items-center gap-4 text-xs text-[hsl(var(--text-muted))]">
                      <span className="flex items-center gap-1"><Users size={12} /> {(poll.total_voters || []).length} voters</span>
                      <span>{poll.user_name}</span>
                      <span className="flex items-center gap-1"><Calendar size={12} /> {poll.created_at ? new Date(poll.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : ''} {poll.created_at ? new Date(poll.created_at).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      {poll.deadline && <span className="flex items-center gap-1"><Clock size={12} /> Due: {new Date(poll.deadline).toLocaleDateString('ms-MY')}</span>}
                    </div>
                    {isActive && !isVoting && (
                      <Button size="sm" data-testid={`vote-btn-${poll.id}`} onClick={() => { setVotingPollId(poll.id); setSelectedOptions([]); }} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs">
                        <BarChart3 size={12} className="mr-1.5" />{voted ? 'Change Vote' : 'Vote'}
                      </Button>
                    )}
                    {isVoting && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setVotingPollId(null); setSelectedOptions([]); }} className="text-xs">Cancel</Button>
                        <Button size="sm" data-testid={`confirm-vote-btn-${poll.id}`} onClick={() => handleVote(poll.id)} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs">Confirm</Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Create Poll Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[hsl(var(--text-primary))] flex items-center gap-2">
                <Vote size={18} className="text-emerald-400" /> New Poll
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Question / Title *</label>
                <Input data-testid="poll-title-input" value={newPoll.title} onChange={(e) => setNewPoll({ ...newPoll, title: e.target.value })} placeholder="e.g. When to do team building?" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Description (optional)</label>
                <textarea value={newPoll.description} onChange={(e) => setNewPoll({ ...newPoll, description: e.target.value })} placeholder="Add context..." rows={2} className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md p-3 text-sm text-[hsl(var(--text-primary))] resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-2 block font-medium">Options *</label>
                <div className="space-y-2">
                  {newPoll.options.map((opt, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input data-testid={`poll-option-${idx}`} value={opt} onChange={(e) => { const opts = [...newPoll.options]; opts[idx] = e.target.value; setNewPoll({ ...newPoll, options: opts }); }} placeholder={`Option ${idx + 1}`} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                      {newPoll.options.length > 2 && <Button variant="ghost" size="icon" onClick={() => removeOptionField(idx)} className="shrink-0"><X size={14} /></Button>}
                    </div>
                  ))}
                  {newPoll.options.length < 10 && <Button variant="outline" size="sm" onClick={addOptionField} className="w-full border-dashed border-[hsl(var(--border))]"><Plus size={14} className="mr-1" /> Add</Button>}
                </div>
              </div>
              <div className="space-y-3 pt-2 border-t border-[hsl(var(--border))]">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={newPoll.is_multiple_choice} onChange={(e) => setNewPoll({ ...newPoll, is_multiple_choice: e.target.checked })} className="w-4 h-4 rounded accent-emerald-500" />
                  <span className="text-sm text-[hsl(var(--text-primary))]">Allow multiple choices</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={newPoll.is_anonymous} onChange={(e) => setNewPoll({ ...newPoll, is_anonymous: e.target.checked })} className="w-4 h-4 rounded accent-emerald-500" />
                  <span className="text-sm text-[hsl(var(--text-primary))]">Anonymous voting</span>
                </label>
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Deadline (optional)</label>
                <Input type="datetime-local" value={newPoll.deadline} onChange={(e) => setNewPoll({ ...newPoll, deadline: e.target.value })} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button data-testid="submit-poll-btn" onClick={handleCreatePoll} disabled={submitting} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null} Create Poll
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ResponsiveLayout>
  );
}
